import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Chip,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import {
  ExternalReceiverProfile,
  getReceiverOverview,
  ReceiverOverviewPayload,
} from '../../../../common/src/api/external-profiles';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  Alert,
  EmptyState,
  KpiCard,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../../common/src/components/ui';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { compareSeasonWeeks } from '../../../../common/src/utils/seasonWeekOrder';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';

interface ReceiverOverviewPageProps {
  profile: ExternalReceiverProfile;
}

const emptyOverview: ReceiverOverviewPayload = {
  kpis: {
    total_shipments: 0,
    total_containers: 0,
    total_boxes: 0,
    total_kg: 0,
    pending_shipments: 0,
    settled_shipments: 0,
    arriving_next_14_days: 0,
    arriving_next_14_days_kg: 0,
    total_committed: 0,
    total_shipped_equivalent_boxes: 0,
    fulfillment_pct: null,
    remaining_boxes: 0,
    active_exporters: 0,
  },
  weekly_series: [],
  fulfillment: [],
  upcoming_arrivals: [],
  alerts: [],
};

const getLocale = (language?: string) => {
  if (language?.startsWith('es')) return 'es-CL';
  if (language?.startsWith('en')) return 'en-US';
  return 'zh-CN';
};

const formatNumber = (
  value: number,
  locale: string,
  maximumFractionDigits = 0
) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits }).format(
    Number(value) || 0
  );

const formatPercent = (value: number | null | undefined, locale: string) =>
  value == null ? '-' : `${formatNumber(value, locale, 1)}%`;

const formatDate = (value: string | null | undefined, locale: string) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const ReceiverOverviewPage = ({ profile }: ReceiverOverviewPageProps) => {
  const { t, currentLanguage } = useTranslation('receiver');
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const locale = useMemo(() => getLocale(currentLanguage), [currentLanguage]);
  const [overview, setOverview] = useState<ReceiverOverviewPayload>(emptyOverview);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [selectedExporter, setSelectedExporter] = useState('all');
  const [selectedClient, setSelectedClient] = useState('all');

  const scopedRelationships = useMemo(() => {
    if (!selectedSeason || !selectedSpecies?.id) return [];
    return profile.relationships.filter((relationship) =>
      (relationship.scopes || []).some(
        (scope) =>
          scope.season === selectedSeason &&
          scope.species_id === selectedSpecies.id
      )
    );
  }, [profile.relationships, selectedSeason, selectedSpecies?.id]);

  const exporters = useMemo(() => {
    const map = new Map<number, string>();
    scopedRelationships.forEach((relationship) => {
      map.set(relationship.exporter_id, relationship.exporter_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [scopedRelationships]);

  const clients = useMemo(() => {
    return scopedRelationships.filter(
      (relationship) =>
        selectedExporter === 'all' ||
        relationship.exporter_id === Number(selectedExporter)
    );
  }, [scopedRelationships, selectedExporter]);

  useEffect(() => {
    if (
      selectedExporter !== 'all' &&
      !exporters.some((exporter) => exporter.id === Number(selectedExporter))
    ) {
      setSelectedExporter('all');
    }
  }, [exporters, selectedExporter]);

  useEffect(() => {
    if (
      selectedClient !== 'all' &&
      !clients.some(
        (client) => client.exporter_receiver_id === Number(selectedClient)
      )
    ) {
      setSelectedClient('all');
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    const load = async () => {
      if (!selectedSeason || !selectedSpecies?.id) {
        setOverview(emptyOverview);
        setErrorKey(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorKey(null);
      try {
        const payload = await getReceiverOverview({
          season: selectedSeason,
          speciesId: selectedSpecies.id,
          exporterId:
            selectedExporter === 'all' ? null : Number(selectedExporter),
          exporterReceiverIds:
            selectedClient === 'all' ? null : [Number(selectedClient)],
        });
        // La RPC ordena W53 y W1 en la misma posición: en temporadas con
        // semana 53 se reordena aquí (52 → 53 → 1).
        setOverview({
          ...payload,
          weekly_series: [...payload.weekly_series].sort((a, b) =>
            compareSeasonWeeks(Number(a.week), Number(b.week))
          ),
        });
      } catch (loadError) {
        console.error('Error loading receiver overview:', loadError);
        setErrorKey('overview.loadError');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedSeason, selectedSpecies?.id, selectedExporter, selectedClient]);

  const maxWeeklyValue = useMemo(() => {
    return Math.max(
      1,
      ...overview.weekly_series.map((point) =>
        Math.max(Number(point.committed) || 0, Number(point.shipped) || 0)
      )
    );
  }, [overview.weekly_series]);

  const kpiCards = [
    {
      label: t('overview.kpis.totalShipped'),
      value: formatNumber(overview.kpis.total_shipped_equivalent_boxes, locale),
      detail: `${formatNumber(overview.kpis.total_kg, locale)} ${t('shared.kg')}`,
    },
    {
      label: t('overview.kpis.inTransit'),
      value: formatNumber(overview.kpis.pending_shipments, locale),
      detail: t('overview.kpis.settledDetail', {
        count: formatNumber(overview.kpis.settled_shipments, locale),
      }),
    },
    {
      label: t('overview.kpis.arrivingNext14Days'),
      value: formatNumber(overview.kpis.arriving_next_14_days, locale),
      detail: `${formatNumber(overview.kpis.arriving_next_14_days_kg, locale)} ${t('shared.kg')}`,
    },
    {
      label: t('overview.kpis.fulfillment'),
      value: formatPercent(overview.kpis.fulfillment_pct, locale),
      detail: t('overview.kpis.committedDetail', {
        count: formatNumber(overview.kpis.total_committed, locale),
      }),
    },
    {
      label: t('overview.kpis.balance'),
      value: formatNumber(overview.kpis.remaining_boxes, locale),
      detail: t('shared.equivalentBoxes'),
    },
    {
      label: t('overview.kpis.exporters'),
      value: formatNumber(overview.kpis.active_exporters, locale),
      detail: t('overview.kpis.shipmentsDetail', {
        count: formatNumber(overview.kpis.total_shipments, locale),
      }),
    },
  ];

  return (
    <PageLayout className='h-full overflow-auto'>
      <PageHeader
        title={t('overview.title')}
        description={selectedSeason}
      />

      <div className='flex flex-col md:flex-row gap-3 mb-5'>
        <Select
          label={t('shared.exporter')}
          className='max-w-xs'
          selectedKeys={[selectedExporter]}
          onChange={(event) => setSelectedExporter(event.target.value || 'all')}
        >
          <SelectItem key='all'>{t('shared.allExporters')}</SelectItem>
          {exporters.map((exporter) => (
            <SelectItem key={String(exporter.id)}>{exporter.name}</SelectItem>
          ))}
        </Select>
        <Select
          label={t('shared.client')}
          className='max-w-xs'
          selectedKeys={[selectedClient]}
          onChange={(event) => setSelectedClient(event.target.value || 'all')}
        >
          <SelectItem key='all'>{t('shared.allClients')}</SelectItem>
          {clients.map((client) => (
            <SelectItem key={String(client.exporter_receiver_id)}>
              {client.client_name}
            </SelectItem>
          ))}
        </Select>
      </div>

      {loading ? (
        <LoadingState />
      ) : errorKey ? (
        <Alert variant='danger' showIcon>
          {t(errorKey)}
        </Alert>
      ) : (
        <div className='space-y-6'>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3'>
            {kpiCards.map((card) => (
              <KpiCard
                key={card.label}
                label={card.label}
                value={card.value}
                detail={card.detail}
              />
            ))}
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-3 gap-5'>
            <Card shadow='sm' className='xl:col-span-2'>
              <CardBody className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-base font-semibold text-foreground'>
                  {t('overview.weekly.title')}
                </h2>
                <Chip variant='flat'>{t('shared.boxes')}</Chip>
              </div>
              {overview.weekly_series.length === 0 ? (
                <EmptyState title={t('overview.weekly.empty')} />
              ) : (
                <div className='overflow-x-auto'>
                  <div
                    className='grid gap-3 min-w-[720px]'
                    style={{
                      gridTemplateColumns: `repeat(${overview.weekly_series.length}, minmax(42px, 1fr))`,
                    }}
                  >
                    {overview.weekly_series.map((point) => {
                      const committedHeight =
                        (Number(point.committed) / maxWeeklyValue) * 160;
                      const shippedHeight =
                        (Number(point.shipped) / maxWeeklyValue) * 160;
                      return (
                        <div
                          key={point.week}
                          className='flex flex-col items-center gap-2'
                        >
                          <div className='h-40 w-full flex items-end justify-center gap-1 border-b border-default-200'>
                            <div
                              className='w-3 rounded-t bg-default-300'
                              style={{ height: Math.max(committedHeight, 2) }}
                              title={t('overview.weekly.committedTitle', {
                                value: formatNumber(point.committed, locale),
                              })}
                            />
                            <div
                              className='w-3 rounded-t bg-brand-700'
                              style={{ height: Math.max(shippedHeight, 2) }}
                              title={t('overview.weekly.shippedTitle', {
                                value: formatNumber(point.shipped, locale),
                              })}
                            />
                          </div>
                          <p className='text-xs text-default-500'>{point.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className='flex gap-4 mt-4 text-xs text-default-500'>
                <span className='inline-flex items-center gap-1'>
                  <span className='h-2 w-2 rounded-full bg-default-300' />
                  {t('shared.committed')}
                </span>
                <span className='inline-flex items-center gap-1'>
                  <span className='h-2 w-2 rounded-full bg-brand-700' />
                  {t('shared.shipped')}
                </span>
              </div>
              </CardBody>
            </Card>

            <Card shadow='sm'>
              <CardBody className='p-6'>
              <h2 className='text-base font-semibold text-foreground mb-4'>
                {t('overview.alerts.title')}
              </h2>
              {overview.alerts.length === 0 ? (
                <EmptyState title={t('overview.alerts.empty')} />
              ) : (
                <div className='space-y-3'>
                  {overview.alerts.slice(0, 8).map((alert, index) => (
                    <div
                      key={`${alert.type}-${index}`}
                      className='border border-default-200 rounded-md p-3'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div>
                          <p className='text-sm font-medium text-foreground'>
                            {t(`overview.alerts.types.${alert.type}`, {
                              defaultValue: alert.title,
                            })}
                          </p>
                          <p className='text-xs text-default-500'>
                            {alert.exporter_name} / {alert.client_name}
                          </p>
                        </div>
                        <Chip size='sm' variant='flat'>
                          {t(`overview.alerts.types.${alert.type}`, {
                            defaultValue: alert.type.replaceAll('_', ' '),
                          })}
                        </Chip>
                      </div>
                      {(alert.reference_number || alert.container_number) && (
                        <p className='text-xs text-default-500 mt-2'>
                          {alert.reference_number || '-'} /{' '}
                          {alert.container_number || '-'}
                        </p>
                      )}
                      {alert.balance != null && (
                        <p className='text-xs text-default-500 mt-2'>
                          {t('overview.alerts.balance', {
                            value: formatNumber(alert.balance, locale),
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </CardBody>
            </Card>
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>
            <Card shadow='sm'>
              <CardBody className='p-6'>
              <h2 className='text-base font-semibold text-foreground mb-4'>
                {t('overview.upcomingArrivals.title')}
              </h2>
              <Table removeWrapper aria-label={t('overview.upcomingArrivals.ariaLabel')}>
                <TableHeader>
                  <TableColumn>{t('shared.eta')}</TableColumn>
                  <TableColumn>{t('shared.exporter')}</TableColumn>
                  <TableColumn>{t('shared.reference')}</TableColumn>
                  <TableColumn>{t('shared.kg')}</TableColumn>
                </TableHeader>
                <TableBody
                  emptyContent={t('overview.upcomingArrivals.empty')}
                  items={overview.upcoming_arrivals}
                >
                  {(arrival) => (
                    <TableRow key={arrival.shipment_id}>
                      <TableCell>{formatDate(arrival.ship_eta, locale)}</TableCell>
                      <TableCell>
                        <div>
                          <p className='text-sm'>{arrival.exporter_name}</p>
                          <p className='text-xs text-default-500'>
                            {arrival.client_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{arrival.reference_number || '-'}</TableCell>
                      <TableCell>{formatNumber(arrival.total_kg, locale)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </CardBody>
            </Card>

            <Card shadow='sm'>
              <CardBody className='p-6'>
              <h2 className='text-base font-semibold text-foreground mb-4'>
                {t('overview.fulfillment.title')}
              </h2>
              <Table removeWrapper aria-label={t('overview.fulfillment.ariaLabel')}>
                <TableHeader>
                  <TableColumn>{t('overview.fulfillment.exporterClient')}</TableColumn>
                  <TableColumn>{t('shared.committed')}</TableColumn>
                  <TableColumn>{t('shared.shipped')}</TableColumn>
                  <TableColumn>{t('shared.balance')}</TableColumn>
                  <TableColumn>%</TableColumn>
                </TableHeader>
                <TableBody
                  emptyContent={t('overview.fulfillment.empty')}
                  items={overview.fulfillment}
                >
                  {(row) => (
                    <TableRow key={`${row.exporter_id}-${row.exporter_receiver_id}`}>
                      <TableCell>
                        <div>
                          <p className='text-sm'>{row.exporter_name}</p>
                          <p className='text-xs text-default-500'>
                            {row.client_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{formatNumber(row.committed, locale)}</TableCell>
                      <TableCell>{formatNumber(row.shipped, locale)}</TableCell>
                      <TableCell>{formatNumber(row.balance, locale)}</TableCell>
                      <TableCell>{formatPercent(row.fulfillment_pct, locale)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ReceiverOverviewPage;
